
import { Component, Prop, Watch, Vue } from 'vue-property-decorator';
import { NewClientModalTabs } from '@/enums/components/EditClient/NewClientModalTabs';
import CustomerInformation from '@/components/global/client/CustomerInformation.vue';
import ContactInformation from '@/components/global/client/ContactInformation.vue';
import AdditionalInformation from '@/components/global/client/AdditionalInformation.vue';
import DiscountInformation from '@/components/global/client/DiscountInformation.vue';
import Currency from '@/models/Currency';
import PaymentType from '@/models/PaymentType';
import BankAccount from '@/models/BankAccount';
import Country from '@/models/Country';
import PriceType from '@/models/PriceType';
import PriceListRegion from '@/models/PriceListRegion';
import ContactPerson from '@/models/ContactPerson';
import ContactConfig from '@/models/interfaces/Contact';
import AddressConfig from '@/models/interfaces/Address';
import Address from '@/models/Address';
import Discount from '@/models/Discount';
import Client from '@/models/Client';
import { RouteNames } from '@/enums/routes/RouteNames';
import { AvailableClientFields } from '@/interfaces/components/clients/AvailableClientFields';
import ClientType from '@/models/ClientType';
import AddressType from '@/models/AddressType';
import ClientTypeRepository from '@/repositories/ClientTypeRepository';
import PaymentTypeRepository from '@/repositories/PaymentTypeRepository';
import AddressTypeRepository from '@/repositories/AddressTypeRepository';
import ProductGroup from '@/models/ProductGroup';
import UserGroupRepository from '@/repositories/UserGroupRepository';
import { LoadingOverlayHelper } from '@/helpers/LoadingOverlayHelper';
import ClientRepository from '@/repositories/ClientRepository';
import ContactPersonRepository from '@/repositories/ContactPersonRepository';
import { EventBus } from '@/helpers/EventBusHelper';
import { EventBusEvents } from '@/enums/global/EventBusEvents';
import { PopupEvents } from '@/enums/global/PopupEvents';
import CMSUser from '@/models/CMSUser';
import { ClientAdditionalInformation } from '@/interfaces/components/clients/ClientAdditionalInformation';
import { ErrorOptions } from '@/interfaces/ErrorOptions';
import { DiscountTableEntry } from '@/interfaces/components/clients/DiscountTableEntry';
import { webpageValidationRegex, emailValidationRegex } from '@/regex/formRegex';
import { UserRightsEnum } from '@/enums/global/UserRights';
import DeliveryRegion from '@/models/DeliveryRegion';

@Component({
    name: 'NewClientModal',
    components: { CustomerInformation, ContactInformation, AdditionalInformation, DiscountInformation },
})
export default class NewClientModal extends Vue {
    @Prop({ default: false }) private isNewClientModalVisible!: boolean;

    private emailValidationOverride: boolean = false;
    private emailValidationOverrideValue: boolean = false;
    private formData: any = { ...this.emptyFormData() };

    private mailFormat = emailValidationRegex;
    private loadingOverlay = new LoadingOverlayHelper(this, {});
    private validationErrors: string[] = [];
    private refErrors: string[] = [];
    private activeTab = 'Customer';
    private contactErrors = [];
    private isLoading = true;
    private invoiceEmailErrors = {
        abEmail: false,
        dobavnicaEmail: false,
        invoiceEmail: false,
    };

    @Watch('isNewClientModalVisible', { immediate: true })
    private handleModalOpen(newVal: boolean, oldVal: boolean) {
        if (newVal && !oldVal) {
            this.loadingOverlay.start();
            let defaultCountry;
            const currentUGCode = String(this.$store.getters['jwtData/currentUserGroup']);
            const currentUserGroup = UserGroupRepository.getById(currentUGCode);

            // Hardcoded default country for Italy user group
            if (currentUGCode === '4') {
                defaultCountry = '15';
            } else {
                if (currentUserGroup?.client?.address?.country.id) {
                    defaultCountry = currentUserGroup.client?.address?.country.id;
                } else {
                    defaultCountry = currentUserGroup?.clientCountryId?.id;
                }
            }

            if (defaultCountry) {
                // @ts-ignore
                this.formData.clientAddresses[0].country = defaultCountry;
            } else {
                this.formData.clientAddresses[0].country = '23';
            }
            this.loadingOverlay.stop();
        }
    }

    @Watch('formData.isCompany', {
        immediate: false,
    })
    private onUserTypeChange(isCompany: boolean) {
        if (!isCompany) {
            this.formData.webpage = '';
        }
    }

    private get tabs() {
        const allTabs = Object.values(NewClientModalTabs);

        if (this.formData.isCompany) {
            return allTabs.filter((tab) => tab !== NewClientModalTabs.FAMILY);
        }

        return allTabs.filter((tab) => tab !== NewClientModalTabs.CONTACT);
    }

    private get defaultCountry() {
        const currentUserGroup = UserGroupRepository.getById(String(this.$store.getters['jwtData/currentUserGroup']));
        return (
            currentUserGroup?.client?.address?.country?.id ||
            currentUserGroup?.clientCountryId?.id ||
            this.formData.clientAddresses[0]?.country || 
            '23' //SI
        );
    }

    private get isItalyUG() {
        return this.$store.getters['jwtData/isItalyUG'];
    }

    private get userRights() {
        return this.$store.getters['jwtData/userRights'];
    }

    private get canAccessLogistics() {
        return this.userRights.includes(UserRightsEnum.ACCESS_LOGISTICS);
    }

    private emptyFormData() {
        return {
            isCompany: false,
            name: null,
            clientAddresses: [
                {
                    streetNameAndNumber: '',
                    poNumber: '',
                    city: '',
                    building: '',
                    country: '',
                    isDefault: true,
                    b2bDeliverySchedule: '',
                    b2bDeliveryRegion: '',
                } as AddressConfig,
            ],
            // Legacy address fields for backward compatibility
            addressForm: {
                id: null,
                streetNameAndNumber: null,
                poNumber: null,
                city: null,
                building: null,
                country: null,
            },
            address: null,
            taxId: null,
            registerId: null,
            isTaxPayer: true,
            bankCode: null,
            currency: null,
            paymentType: null,
            priceType: null,
            priceListRegion: null,
            assignedUser: null,
            salesman: null,
            tags: [],
            additionalNotes: null,
            contacts: [],
            discounts: [],
            contactPersons: [''],
            discount_ids: [''],
            clientTypes: [],
            webpage: '',
            // bigLogo: null,
            // squareLogo: null,
            roltekAppPartner: false,
            backofficeUser: null,
            leadOrigin: null,
            taxCodeWithoutPrefix: null,
            taxCountryPrefix: '',
            abEmail: { email: '', emailType: 'AB' },
            dobavnicaEmail: { email: '', emailType: 'DOBAVNICA' },
            invoiceEmail: { email: '', emailType: 'INVOICE' },
            useDifferentEmailsForInvoices: false,
        };
    }

    private openDuplicateClientPopup(duplicate: Client) {
        EventBus.$emit(EventBusEvents.openDuplicateClientPopup, {
            popupEvent: PopupEvents.openDuplicateClient,
            data: {
                client: duplicate,
            },
        });
    }

    private setFieldsBorder(
        arr: string[],
        addressErrors: Array<{ ref: string; addressIndex: number }>,
        clear: boolean = false,
    ) {
        let customerInfo = this.$refs.customerinfo as any;

        if (Array.isArray(customerInfo)) {
            customerInfo = customerInfo[0];
        }

        if (!customerInfo) {
            return;
        }

        const borderStyle = clear ? '1px solid #d9d9d9' : '1px solid red';

        arr.forEach((item: string) => {
            if (item === 'phone') {
                const phoneWrapper = document.getElementById('phone-number-wrapper');
                if (phoneWrapper) {
                    phoneWrapper.style.border = borderStyle;
                }
                return;
            }

            // Handle 'name' field which is inside customerTypeSection
            if (item === 'name') {
                const customerTypeSection = customerInfo.$refs.customerTypeSection as any;
                if (customerTypeSection) {
                    const el = customerTypeSection.nameInput || customerTypeSection.$refs?.nameInput;
                    if (el && el.$el) {
                        el.$el.style.border = borderStyle;
                    } else if (el && el.style) {
                        el.style.border = borderStyle;
                    }
                }
                return;
            }

            // Handle 'tax' field which is inside taxesPaymentSection
            if (item === 'tax') {
                const taxesPaymentSection = customerInfo.$refs.taxesPaymentSection as any;
                if (taxesPaymentSection) {
                    const el = taxesPaymentSection.tax || taxesPaymentSection.$refs?.tax;
                    if (el && el.$el) {
                        el.$el.style.border = borderStyle;
                    } else if (el && el.style) {
                        el.style.border = borderStyle;
                    }
                }
                return;
            }

            const el = customerInfo.$refs[item] as any;

            if (el && el instanceof Vue) {
                el.$el.style.border = borderStyle;
            } else if (el && el.$el) {
                el.$el.style.border = borderStyle;
            }
        });

        addressErrors.forEach((item) => {
            const addressFormRef = `addressForm-${item.addressIndex}`;
            let addressForm = customerInfo.$refs[addressFormRef] as any;

            // Handle array refs (Vue wraps v-for refs in arrays)
            if (Array.isArray(addressForm)) {
                addressForm = addressForm[0];
            }

            if (!addressForm) {
                return;
            }

            // Try to find the ref directly on addressForm
            let el = addressForm.$refs[item.ref] as any;

            // If not found, try in addressLocationFields child component
            if (!el && addressForm.$refs.addressLocationFields) {
                const locationFields = addressForm.$refs.addressLocationFields as any;
                // Handle both Vue 2 style refs and Vue 3 exposed refs
                if (locationFields[item.ref]) {
                    el = locationFields[item.ref];
                } else if (locationFields.$refs && locationFields.$refs[item.ref]) {
                    el = locationFields.$refs[item.ref];
                }
            }

            // Handle Vue 3 ref objects (have .value property)
            if (el && el.value && el.value.$el) {
                el = el.value;
            }

            if (el && el instanceof Vue) {
                el.$el.style.border = borderStyle;
            } else if (el && el.$el) {
                el.$el.style.border = borderStyle;
            } else if (el && el.style) {
                el.style.border = borderStyle;
            }
        });
    }

    private confirmFieldsAreValid() {
        this.validationErrors = [];
        this.refErrors = [];
        const addressErrors: Array<{ ref: string; addressIndex: number }> = [];

        let flag = true;
        let customerIsValid = true;
        let contactInfoIsValid = true;

        if (!this.formData.name) {
            flag = false;
            customerIsValid = false;
            this.validationErrors.push(this.$t('Polje ime je obavezno'));
            this.refErrors.push('name');
        }

        // Validate clientAddresses array
        if (!this.formData.clientAddresses || this.formData.clientAddresses.length === 0) {
            flag = false;
            customerIsValid = false;
            this.validationErrors.push(this.$t('At least one address is required'));
        } else {
            this.formData.clientAddresses.forEach((address: any, index: number) => {
                if (!address.streetNameAndNumber) {
                    flag = false;
                    customerIsValid = false;
                    this.validationErrors.push(this.$t('Polje naziv ulice i broj je obavezno'));
                    addressErrors.push({ ref: 'addressStreetNameAndNumber', addressIndex: index });
                }

                if (!address.poNumber) {
                    flag = false;
                    customerIsValid = false;
                    this.validationErrors.push(this.$t('Polje poštanski broj je obavezno'));
                    addressErrors.push({ ref: 'addressPoNumber', addressIndex: index });
                }

                if (!address.city) {
                    flag = false;
                    customerIsValid = false;
                    this.validationErrors.push(this.$t('Polje grad je obavezno'));
                    addressErrors.push({ ref: 'addressCity', addressIndex: index });
                }

                if (!address.country) {
                    flag = false;
                    customerIsValid = false;
                    this.validationErrors.push(this.$t('Polje država je obavezno'));
                    addressErrors.push({ ref: 'country', addressIndex: index });
                }
            });
        }

        let missingContactInfo = false;
        let missingAddressInfo = false;

        const contactErrors: any = [];
        this.formData.contacts.forEach((contact: any, i: number) => {
            const isContactValid = !!contact.name && !!contact.email && !!contact.phone;

            const isAddressValid =
                !!contact.address.streetNameAndNumber &&
                !!contact.address.poNumber &&
                !!contact.address.city &&
                !!contact.address.country;

            if (!isContactValid) {
                missingContactInfo = true;
            }

            if (!isAddressValid && !contact.useDefaultAddress) {
                missingAddressInfo = true;
            }

            contactErrors.push({ index: i, errors: this.checkContactFieldsForErrors(contact) });
        });

        this.contactErrors = contactErrors;

        if (missingContactInfo) {
            flag = false;
            contactInfoIsValid = false;
            this.validationErrors.push(this.$t('Polja imena, emaila i telefona su obavezna'));
        }

        if (missingAddressInfo) {
            flag = false;
            contactInfoIsValid = false;
            this.validationErrors.push(this.$t('Polja adrese kontakt osobe su obavezna'));
        }

        // @todo check every contact
        if (!this.formData.contacts.length) {
            flag = false;
            contactInfoIsValid = false;
            this.validationErrors.push(this.$t('Nedostaje kontakt osoba'));
            this.refErrors.push('contacts');
        }

        if (this.formData.isCompany) {
            const webpageValid = this.validateWebpage();

            if (!webpageValid) {
                flag = false;
                customerIsValid = false;
                this.validationErrors.push(this.$t('Invalid webpage!'));
                this.refErrors.push('webpage');
            }
        }

        if (this.formData.isCompany || this.isItalyUG) {
            if (!this.formData.taxId) {
                flag = false;
                customerIsValid = false;
                this.validationErrors.push(this.$t('Polje porezni id broj je obavezno'));
                this.refErrors.push('tax');
            }
        }

        // Clear previous borders on all fields
        const allClearableRefs = ['name', 'phone', 'tax', 'contacts'];
        const allAddressRefs = ['addressStreetNameAndNumber', 'addressPoNumber', 'addressCity', 'country'];
        const allAddressErrors: Array<{ ref: string; addressIndex: number }> = [];
        if (this.formData.clientAddresses) {
            this.formData.clientAddresses.forEach((_: any, index: number) => {
                allAddressRefs.forEach((ref) => {
                    allAddressErrors.push({ ref, addressIndex: index });
                });
            });
        }
        this.setFieldsBorder(allClearableRefs, allAddressErrors, true);

        // Set red borders on invalid fields
        if (!flag) {
            this.setFieldsBorder(this.refErrors, addressErrors, false);
        }

        this.switchToCorrectTab(customerIsValid, contactInfoIsValid);

        return flag;
    }

    private validateWebpage() {
        const input = this.formData.webpage;

        if (!input) {
            return true;
        }

        if (!webpageValidationRegex.test(input)) {
            return false;
        }

        return true;
    }

    private checkContactFieldsForErrors(contact: any) {
        const errorState: any = {};

        if (!contact.email) {
            errorState.email = true;
        }

        if (!contact.phone) {
            errorState.phone = true;
        }

        if (!contact.name) {
            errorState.name = true;
        }

        if (contact.useDefaultAddress) {
            return errorState;
        }

        if (!contact.address?.city) {
            errorState.city = true;
        }

        if (!contact.address?.country) {
            errorState.country = true;
        }

        if (!contact.address?.poNumber) {
            errorState.poNumber = true;
        }

        if (!contact.address?.streetNameAndNumber) {
            errorState.streetNameAndNumber = true;
        }

        return errorState;
    }

    private checkInvoiceEmailsForErrors() {
        const errorState = {
            abEmail: false,
            dobavnicaEmail: false,
            invoiceEmail: false,
        };

        // Check AB email
        if (this.formData.abEmail?.email && !this.mailFormat.test(this.formData.abEmail?.email)) {
            errorState.abEmail = true;
        }

        // Check dobavnica email
        if (this.formData.dobavnicaEmail?.email && !this.mailFormat.test(this.formData.dobavnicaEmail?.email)) {
            errorState.dobavnicaEmail = true;
        }

        // Check invoice email
        if (this.formData.invoiceEmail?.email && !this.mailFormat.test(this.formData.invoiceEmail?.email)) {
            errorState.invoiceEmail = true;
        }

        return errorState;
    }

    private switchToCorrectTab(customerIsValid: boolean, contactInfoIsValid: boolean) {
        let contactTab = '';

        if (this.formData?.isCompany) {
            contactTab = NewClientModalTabs.CONTACT;
        } else {
            contactTab = NewClientModalTabs.FAMILY;
        }

        switch (this.activeTab) {
            case 'Customer':
                if (!customerIsValid) {
                    return;
                }

                if (!contactInfoIsValid) {
                    this.activeTab = contactTab;
                }
                break;
            case contactTab:
                if (!contactInfoIsValid) {
                    return;
                }

                if (!customerIsValid) {
                    this.activeTab = 'Customer';
                }
            default:
                if (!customerIsValid) {
                    this.activeTab = 'Customer';
                }

                if (!contactInfoIsValid) {
                    this.activeTab = contactTab;
                }
        }
    }

    private async confirmEmailsAreValid() {
        const contactEmails = this.formData.contacts.map((contact: any) => {
            return contact.email;
        });

        const emails = [...contactEmails];
        const clients: Client[] = [];

        for await (const email of emails) {
            if (email && !email.match(this.mailFormat)) {
                this.$notification.error({
                    message: this.$t('Invalid email') as string,
                    description: '',
                });
                this.loadingOverlay.stop();

                return false;
            }

            const duplicate = await this.checkDuplicateEmails(email);
            if (duplicate) {
                clients.push(duplicate);

                this.openDuplicateClientPopup(duplicate);
                this.loadingOverlay.stop();

                return false;
            }
        }

        if (clients.length === 0) {
            return Promise.resolve(true);
        }
    }

    private async checkDuplicateEmails(input: string) {
        const customerName = this.formData.name;
        const customerAddress = this.formData.addressForm.streetNameAndNumber;
        const customerTaxId = this.formData.taxId;
        let clients = null;

        try {
            if (this.formData.isCompany && Array.isArray(this.formData.contacts)) {
                for (const contact of this.formData.contacts) {
                    const result = await Client.validateClient(
                        contact.email,
                        contact.phone,
                        customerName,
                        customerAddress,
                        customerTaxId,
                    );
                    if (result && result.data.length > 0) {
                        return result.data[0];
                    }
                }
            } else {
                clients = await Client.validateClient(
                    input,
                    this.formData.phone,
                    customerName,
                    customerAddress,
                    customerTaxId,
                );
                return clients.data.length <= 0 ? null : clients.data[0];
            }
        } catch (e) {
            console.error('Error checking duplicates', e);
            return null;
        }

        return null;
    }

    private isCustomerInfoShown(tab: string) {
        return tab === NewClientModalTabs.CUSTOMER;
    }
    private isContactInfoShown(tab: string) {
        return tab === NewClientModalTabs.CONTACT || tab === NewClientModalTabs.FAMILY;
    }
    private isAdditionalInfoShown(tab: string) {
        return tab === NewClientModalTabs.ADDITIONAL;
    }
    private isDiscountsShown(tab: string) {
        return tab === NewClientModalTabs.DISCOUNTS;
    }

    private async createNewClientAndDepenencies() {
        this.loadingOverlay.start();

        const data = this.formData as AvailableClientFields;
        this.formatDataForUpdate(data);
        let newClientId;

        if (!this.confirmFieldsAreValid()) {
            this.$notification.error({
                message: this.validationErrors.join('!\n'),
                description: '',
            });

            this.loadingOverlay.stop();

            return;
        }

        const emailsAreValid = await this.confirmEmailsAreValid();

        if (!emailsAreValid) {
            this.loadingOverlay.stop();
            return;
        }

        try {
            await this.createClientAddresses(data);
            data.contactPersons = await this.createContacts();
            newClientId = await this.createClient(data);
            await this.createDiscounts(newClientId);

            // Update client at the end to make sure data is sent to Roltek in case we created
            // it with discounts
            if (this.formData.discounts.length > 0) {
                await Client.updateExisting(newClientId, data);
            }
        } catch (e) {
            const error = e as ErrorOptions;

            this.$notification.error({
                message: this.$t('Dogodila se greška') as string,
                description: error.response ? error.response.data.meta.message : error.message,
            });
            this.loadingOverlay.stop();
            return;
        }

        const isProjectCreation = this.$store.getters['generic/projectClientCreation'];

        if (isProjectCreation) {
            const client = ClientRepository.getById(newClientId);

            this.$store.commit('generic/setCreatedClient', client);
            this.$store.commit('generic/emptyCreatedContacts');

            data.contactPersons.forEach((id: string) => {
                const contact = ContactPersonRepository.getById(id);
                this.$store.commit('generic/addCreatedContact', contact);
            });
        } else {
            this.$router.push({
                name: RouteNames.editClient,
                params: { clientId: newClientId },
            });
        }

        this.closePopup();

        this.$notification.success({
            message: this.$t('Client has been created!') as string,
            description: '',
            class: 'client-create-success',
        });

        this.loadingOverlay.stop();
    }

    private async createClientAddresses(data: AvailableClientFields) {
        if (!data.clientAddresses || data.clientAddresses.length === 0) {
            return;
        }

        const addressPromises = data.clientAddresses
            .filter((addressForm: AddressConfig) => this.hasAddressData(addressForm))
            .map((addressForm: AddressConfig) =>
                Address.createNew(addressForm).then((resp) => {
                    const newAddressId = resp.data.id;
                    addressForm.id = newAddressId; // Update form with new address ID
                    this.mapAddressIdToContacts(addressForm);

                    return newAddressId;
                }),
            );

        const addressIds = await Promise.all(addressPromises);

        data.addresses = addressIds;
    }

    private mapAddressIdToContacts(address: AddressConfig) {
        this.formData.contacts.forEach((contact: ContactConfig) => {
            if (contact.address && contact.useDefaultAddress) {
                if (address.id === contact.address?.id) {
                    return;
                }

                if (!contact.address?._localId || !address?._localId) {
                    return;
                }

                if (contact.address?._localId === address._localId) {
                    contact.address.id = address.id;
                }
            }
        });
    }

    private async createClientDespiteDuplicates() {
        this.loadingOverlay.start();

        const data = this.formData as AvailableClientFields;
        this.formatDataForUpdate(data);
        let newClientId;

        try {
            await this.createClientAddresses(data);

            data.contactPersons = await this.createContacts();

            newClientId = await this.createClient(data);

            await this.createDiscounts(newClientId);

            // Update client at the end to make sure data is sent to Roltek in case we created
            // it with discounts
            if (this.formData.discounts.length > 0) {
                await Client.updateExisting(newClientId, data);
            }
        } catch (e) {
            console.error('Error in createClientDespiteDuplicates', e);
            const error = e as ErrorOptions;

            this.$notification.error({
                message: this.$t('Dogodila se greška') as string,
                description: error.response ? error.response.data.meta.message : error.message,
            });
            this.loadingOverlay.stop();
            return;
        }

        const isProjectCreation = this.$store.getters['generic/projectClientCreation'];

        if (isProjectCreation) {
            const client = ClientRepository.getById(newClientId);

            this.$store.commit('generic/setCreatedClient', client);
            this.$store.commit('generic/emptyCreatedContacts');

            data.contactPersons.forEach((id: string) => {
                const contact = ContactPersonRepository.getById(id);
                this.$store.commit('generic/addCreatedContact', contact);
            });
        } else {
            this.$router.push({
                name: RouteNames.editClient,
                params: { clientId: newClientId },
            });
        }

        this.closePopup();

        this.$notification.success({
            message: this.$t('Client has been created!') as string,
            description: '',
            duration: 3,
            class: 'client-create-success',
        });

        this.loadingOverlay.stop();
    }

    private formatDataForUpdate(data: AvailableClientFields) {
        if (data.addressForm && !data.addressForm.country) {
            data.addressForm.country = this.defaultCountry;
        }

        if (typeof data.tags[0] !== 'string') {
            data.tags = data.tags.map((tag: any) => tag.name);
        }

        if (this.formData.clientAddresses?.length) {
            this.formData.clientAddresses.forEach((address: any) => {
                if (Array.isArray(address.b2bDeliveryDays) && address.b2bDeliveryDays.length) {
                    address.b2bDeliveryDays = address.b2bDeliveryDays.join('| ').trim();
                } else if (typeof address.b2bDeliveryDays !== 'string') {
                    address.b2bDeliveryDays = '';
                }
            });
        }

        if (this.formData.contacts?.length) {
            this.formData.contacts.forEach((contact: any) => {
                if (
                    contact.address &&
                    Array.isArray(contact.address.b2bDeliveryDays) &&
                    contact.address.b2bDeliveryDays.length
                ) {
                    contact.address.b2bDeliveryDays = contact.address.b2bDeliveryDays.join('| ').trim();
                } else if (contact.address && typeof contact.address.b2bDeliveryDays !== 'string') {
                    contact.address.b2bDeliveryDays = '';
                }
            });
        }
    }

    private async createClient(data: AvailableClientFields) {
        if (data.tags.length > 0) {
            data.clientTypes = data.tags;
        }

        data.paymentType = await this.findPaymentTypeId(data);

        const resp = await Client.createNew(data);
        return resp.data.id;
    }

    private async createContacts() {
        const contacts = [];

        for (const contact of this.formData.contacts as ContactConfig[]) {
            if (!contact?.address) {
                continue;
            }

            if (!contact.useDefaultAddress) {
                if (contact.address?.country && typeof contact.address.country === 'string') {
                    contact.address.country = contact.address.country.split('|')[0];
                }

                const newAddress = await Address.createNew(contact.address);
                contact.address.id = newAddress.data.id;
            } else {
                contact.address.id = this.findDefaultAddressId();
            }

            const newContact = await ContactPerson.createNew(contact);
            contacts.push(newContact.data.id);
        }

        return contacts;
    }

    private findDefaultAddressId() {
        const defaultAddress = this.formData.clientAddresses.find((address: any) => address.isDefault);

        return defaultAddress ? defaultAddress.id : null;
    }

    private async createDiscounts(clientId: string) {
        const discountIds = [];
        for (const discount of this.formData.discounts as DiscountTableEntry[]) {
            // tslint:disable: no-shadowed-variable
            discount.clientId = clientId;
            const resp = await Discount.createNew(discount);
            discountIds.push(resp.data.id);
        }
        return discountIds;
    }

    private hasAddressData(address?: AddressConfig) {
        return (
            !!address?.streetNameAndNumber ||
            !!address?.poNumber ||
            !!address?.city ||
            !!address?.building ||
            !!address?.country
        );
    }

    private async findPaymentTypeId(data: AvailableClientFields) {
        if (!!data.paymentType) {
            return data.paymentType;
        }
        if (this.paymentTypes.find((type) => type.isDefault)) {
            return this.paymentTypes.find((type) => type.isDefault)?.id;
        } else {
            const fetchedDefaultTypes = await PaymentType.fetchDefaultPaymentType();

            return fetchedDefaultTypes.data.find((type: any) => type.isDefault)?.id;
        }
    }

    private get clientTypes() {
        return ClientTypeRepository.getAll();
    }

    private get paymentTypes() {
        return PaymentTypeRepository.getAllWithArchived();
    }

    private get addressTypes() {
        return AddressTypeRepository.getAll();
    }

    private closePopup() {
        if (this.$store.getters['generic/projectClientCreation']) {
            EventBus.$emit(EventBusEvents.toggleNewProjectModal, true);
        }

        this.$store.commit('generic/setProjectClientCreation', false);
        this.formData = this.emptyFormData();

        this.$emit('update:isNewClientModalVisible', false);
    }

    private async fetchInitialClientData() {
        try {
            await Promise.allSettled([
                Currency.getAll(),
                PaymentType.getAll(),
                ClientType.getAll(),
                BankAccount.getAll(),
                Country.getAll(),
                PriceType.getAll(),
                PriceListRegion.getAll(),
                ProductGroup.getAll(),
                CMSUser.getAllOnlyUsers(),
                AddressType.getAll(),
                DeliveryRegion.getAll(),
            ]);
        } catch (e) {
            Promise.reject(e);
        }
    }

    private updateClientData(data: ClientAdditionalInformation) {
        // loop through values and update the form data with the new values if there are fields to update
        for (const key in data.values) {
            if (data.fields.includes(key)) {
                switch (key) {
                    case 'city':
                        this.formData.addressForm!.city = data.values[key];
                        break;
                    case 'country':
                        this.formData.addressForm!.country = String(data.values[key]);
                        break;
                    case 'fullName':
                    case 'shortName':
                        this.formData.name = data.values[key];
                        break;
                    case 'postalCode':
                        this.formData.addressForm!.poNumber = data.values[key];
                        break;
                    case 'street':
                        this.formData.addressForm!.streetNameAndNumber = data.values[key];
                        break;
                    case 'registerID':
                        this.formData.registerId = data.values[key];
                        break;
                    case 'taxNumber':
                        this.formData.taxId = data.values[key];
                        break;
                    case 'taxPayer':
                        this.formData.isTaxPayer = data.values[key] ? true : false;
                        break;
                }
            }
        }
    }

    private logActiveTab(activeTab: any) {
        this.activeTab = activeTab;
    }

    private addDefaultContactBasedOnCustomerData() {
        if (this.formData.contacts.length) {
            return;
        }

        const defaultAddress = this.formData.clientAddresses?.find((address: any) => address.isDefault) || {
            streetNameAndNumber: '',
            poNumber: '',
            city: '',
            building: '',
            country: this.defaultCountry ? this.defaultCountry + '|' : undefined,
            b2bDeliveryPhone: '',
            b2bDeliveryEmail: '',
            b2bDeliveryNotes: '',
            b2bDeliveryDays: [],
            b2bDeliverySchedule: '',
            b2bDeliveryRegion: '',
        };

        const contactData = {
            name: this.formData.name || undefined,
            email: '',
            phone: '',
            job: undefined,
            address: { ...defaultAddress, isDefault: false, addressType: defaultAddress.addressType || '' },
            birthday: '',
            isDefault: true,
            useDefaultAddress: true,
        };

        this.formData.contacts.push(contactData);
    }

    private handleContactsInitialized() {
        this.addDefaultContactBasedOnCustomerData();
    }

    private async created() {
        this.loadingOverlay.start();
        await this.fetchInitialClientData();
        this.isLoading = false;
        this.loadingOverlay.stop();

        EventBus.$on(EventBusEvents.updateClientData, this.updateClientData);
        EventBus.$on(EventBusEvents.saveClientWithDuplicates, this.createClientDespiteDuplicates);
        EventBus.$on(EventBusEvents.closeNewClientPopup, this.closePopup);
    }

    private beforeDestroy() {
        EventBus.$off(EventBusEvents.updateClientData, this.updateClientData);
        EventBus.$off(EventBusEvents.saveClientWithDuplicates, this.createClientDespiteDuplicates);
        EventBus.$off(EventBusEvents.closeNewClientPopup, this.closePopup);
    }
}
